/**
 * Catch image resolver — single source of truth for "what URL should I show
 * for this catch?".
 *
 * Each surface in the app (Community feed, Dashboard recent catches, League
 * Recent Catches, profile dialog, Catch of the Month, winner cards, admin
 * flagged catches, the submit modal preview) had grown its own slightly
 * different version of "look at `image_url`, maybe prefix `/api`, render
 * an <img>". The result: a single legacy catch row that stores its image
 * as `/api/uploads/<uuid>.png` (ephemeral disk, wiped on every redeploy)
 * shows a broken-image placeholder on whichever surface forgot the prefix
 * logic — and even when it doesn't, the file is gone from disk anyway.
 *
 * This helper handles every shape we have in production:
 *
 *   • data URI            -> "data:image/jpeg;base64,...."        (new path)
 *   • absolute URL        -> "https://..."                         (S3 fixtures)
 *   • /api/uploads/...    -> prefix REACT_APP_BACKEND_URL          (legacy)
 *   • bare /uploads/...   -> same                                  (legacy)
 *   • null / empty / junk -> null  (caller renders a branded placeholder)
 *
 * Fields scanned (in order): image_url, image, photo_url, photo, imageData.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

/** Pick whichever image-ish field the row actually has. */
const pickRawImage = (row) => {
  if (!row) return null;
  return row.image_url || row.image || row.photo_url || row.photo || row.imageData || null;
};

/**
 * Resolve to a final renderable URL — or null if the row has no usable image.
 */
export const resolveCatchImage = (row) => {
  const raw = pickRawImage(row);
  if (!raw || typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s) return null;
  if (s.startsWith('data:')) return s;
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  if (s.startsWith('/api/')) return `${API}${s}`;
  if (s.startsWith('/uploads/') || s.startsWith('uploads/')) return `${API}/api${s.startsWith('/') ? '' : '/'}${s}`;
  // Anything else (relative-ish, opaque) — try as-is, the <img onError /> will
  // catch failures and surface the branded placeholder.
  return s;
};

export const catchHasImage = (row) => resolveCatchImage(row) !== null;
